import {type FC, type ReactEventHandler, useEffect} from 'react';
import {useInView} from 'react-intersection-observer';

import {reaction} from 'mobx';
import {Observer} from 'mobx-react';
import styled, {css} from 'styled-components';

import type {WithDomStyleProp} from '../../utils/utility-types';
import type {Props, StyledProps} from './types';

import {useInjection} from '../../IoC';
import {useMobxStore} from '../../utils/store';
import {Text} from '../Text';
import {ImageModelFactory} from './model/ImageModelFactory';
import {
  alt as altStyle,
  blurImage as blurImageStyle,
  image as imageStyle,
  placeholder as placeholderStyle,
  web,
} from './styles';

const Container = styled.div<StyledProps>`
  ${web}
`;

const AltText = styled(Text)`
  ${altStyle}
`;

const StyledImage = styled.img<StyledProps & {resizeMode: Props['resizeMode']}>`
  ${imageStyle};
  ${p =>
    p.resizeMode &&
    css`
      object-fit: ${p.resizeMode};
    `}
`;

const StyledBlurImage = styled.img<StyledProps>`
  ${blurImageStyle};
  object-fit: cover;
`;

const PlaceholderContainer = styled.div<StyledProps>`
  ${placeholderStyle}
`;

const Image: FC<WithDomStyleProp<Props>> = props => {
  const {
    alt,
    animated = true,
    blurFactor,
    blurredCover,
    blurredSrc,
    disableCache,
    onLoad: onLoadProp,
    placeholder,
    resizeMode = 'cover',
    src,
    srcSet,
    testID,
  } = props;

  const factory = useInjection(ImageModelFactory);
  const model = useMobxStore(() => {
    return factory.create();
  });

  const [ref, inView] = useInView();

  useEffect(
    () =>
      reaction(
        () => model.isVisibleFlag.value,
        isVisible => {
          if (isVisible) {
            model.init({blurredCover, blurredSrc, disableCache, src});
          }
        },
        {fireImmediately: true},
      ),
    [src, blurredCover, blurredSrc, model, disableCache],
  );

  useEffect(() => {
    if (inView && model.isVisibleFlag.isFalse) {
      model.isVisibleFlag.setValue(inView);
    }
  }, [inView, model.isVisibleFlag]);

  const renderAlt = () => (
    <Observer>
      {() =>
        placeholder ? (
          <PlaceholderContainer animated={animated} isLoaded={model.isLoaded}>
            {placeholder}
          </PlaceholderContainer>
        ) : model.isValidSrcFlag.isFalse && alt ? (
          <AltText>{alt}</AltText>
        ) : null
      }
    </Observer>
  );

  const renderBlur = () => (
    <Observer>
      {() =>
        model.srcForBlur ? (
          <StyledBlurImage
            animated={animated}
            blurFactor={blurFactor}
            isLoaded={model.isLoaded}
            src={model.srcForBlur as string}
          />
        ) : null
      }
    </Observer>
  );

  const onError: ReactEventHandler<HTMLImageElement> = () => {
    const error = new Error(`Failed to load image: ${src}`);

    model.onError(error);
  };

  const onLoad: ReactEventHandler<HTMLImageElement> = event => {
    model.onLoad();

    if (onLoadProp) {
      const {naturalHeight, naturalWidth} = event.currentTarget;

      onLoadProp({
        height: naturalHeight,
        width: naturalWidth,
      });
    }
  };

  const renderImage = () => (
    <Observer>
      {() =>
        model.isValidSrcFlag.isTrue ? (
          <>
            {renderBlur()}
            <StyledImage
              animated={animated}
              draggable={false}
              isLoaded={model.isLoaded}
              onError={onError}
              onLoad={onLoad}
              resizeMode={resizeMode}
              src={(model.src ?? undefined) as string}
              srcSet={srcSet}
            />
          </>
        ) : null
      }
    </Observer>
  );

  const {onLoad: _, ...containerProps} = props;

  return (
    <Container ref={ref} {...containerProps} data-test-id={testID}>
      {renderAlt()}
      {renderImage()}
    </Container>
  );
};

export {Image};
